import type {
  DynamicFieldComponentProps,
  DynamicFormButtonSetting,
  FileRef,
  JsonDataItem,
} from '@myosh/odin-components';
import { DynamicFormRequiredType, EmptyFieldLabel, FileUpload, OdinDataSender } from '@myosh/odin-components';
import { cloneDeep } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AttachmentItem,
  AttachmentsPayloadProps,
  Confirmations,
  DeletedAttachmentItem,
  PresignedUrlResult,
  StoredObjectProps,
} from '../../../@types/attachments';
import {
  useDeleteAttachmentMutation,
  useGetFilesByRecordIdAndQuestionIdQuery,
  useGetFilesByRecordIdQuery,
  useLazyGetPresignedUrlQuery,
} from '../../../redux/services/file';
import { forceAssert } from '../../../utils/type-guards';
import OdinLoadingIndicator from '../../common/odin-loading-indicator.component';
import type { AttachmentFileProps } from './attachment-list/attachment-list.component';
import AttachmentList from './attachment-list/attachment-list.component';

export interface AttachmentsFieldProps extends DynamicFieldComponentProps<Array<File> | DeletedAttachmentItem> {
  recordId?: string;
  questionId?: number;
  labelStyle?: string;
  onAttachmentsLoaded?: (attachments: AttachmentItem[]) => void;
  thumbnails?: boolean;
  files?: Array<string>; // existing file attachments
  images?: Array<string>; // existing image attachments
  helpMessage?: string;
  setFormButtonsState?: React.Dispatch<React.SetStateAction<DynamicFormButtonSetting[]>>;
  getRecordData: () => JsonDataItem;
  updateRecordDataToIncludeAttachments: (params: AttachmentsPayloadProps) => void;
}

const maxUploadFileSize = 20971520; // equal to 20 MB

const AttachmentsField = ({
  id,
  label,
  labelStyle,
  error,
  recordId,
  registerPreSubmitHandler,
  registerPostSubmitHandler,
  onChange,
  readOnly,
  questionId,
  onAttachmentsLoaded,
  thumbnails = false,
  permissions,
  required = DynamicFormRequiredType.False,
  setFormButtonsState,
  getRecordData,
  updateRecordDataToIncludeAttachments,
}: AttachmentsFieldProps) => {
  const { t } = useTranslation();

  const attachmentRef = useRef<Array<File>>([]);
  const fileRef = useRef<FileRef>(null);
  const deletedAttachmentsRef = useRef<Array<number>>([]);
  const preUploadedAttachmentsRef = useRef<Confirmations[]>([]);
  const storedObjectKeysRef = useRef<StoredObjectProps[]>([]);
  const uploadedFileNamesRef = useRef<string[]>([]);

  //Used as a guard to prevent duplicate handler registration in strict mode
  const preSubmitHandlerRegistered = useRef<boolean>(false);
  const postSubmitHandlerRegistered = useRef<boolean>(false);

  const {
    data: filesWithQuestion,
    isFetching: isFilesWithQuestionFetching,
    isLoading: isFilesWithQuestionsLoading,
  } = useGetFilesByRecordIdAndQuestionIdQuery(
    { recordId: recordId ?? '', questionId: questionId ?? -1 },
    { skip: !recordId || !questionId }
  );
  const {
    data: files,
    isFetching: isFilesFetching,
    isLoading: isFilesLoading,
  } = useGetFilesByRecordIdQuery(
    { recordId: recordId ?? '' },
    { skip: !recordId || (questionId && !isNaN(questionId)) || !permissions?.read }
  );

  const [getPresignedUrl] = useLazyGetPresignedUrlQuery();
  const [deleteAttachment] = useDeleteAttachmentMutation();

  const [attachments, setAttachments] = useState<Array<AttachmentItem>>();
  const [areFilesUploading, setAreFilesUploading] = useState(false);
  const isUploadingOrLoadingFiles = isFilesWithQuestionsLoading || isFilesLoading || areFilesUploading;

  useEffect(() => {
    if (!readOnly) {
      setFormButtonsState?.((prev) =>
        prev.map((button) => ({
          ...button,
          disabled: areFilesUploading,
        }))
      );
    }
  }, [areFilesUploading, readOnly, setFormButtonsState]);

  useEffect(() => {
    let attachmentsNew;
    if (questionId && filesWithQuestion && !isFilesWithQuestionFetching) {
      attachmentsNew = cloneDeep(filesWithQuestion);
    } else if (!questionId && files && !isFilesFetching) {
      attachmentsNew = cloneDeep(files);
    }
    if (attachmentsNew) {
      setAttachments(attachmentsNew);
      onAttachmentsLoaded?.(attachmentsNew);
    }
  }, [filesWithQuestion, isFilesWithQuestionFetching, files, isFilesFetching, questionId]);

  useEffect(() => {
    if (recordId && registerPreSubmitHandler && !preSubmitHandlerRegistered.current) {
      registerPreSubmitHandler((sender) => beforeSubmitFormHandler(Number(recordId), sender));
      preSubmitHandlerRegistered.current = true;
    }
  }, [recordId, registerPreSubmitHandler]);

  useEffect(() => {
    if (registerPostSubmitHandler && !postSubmitHandlerRegistered.current) {
      registerPostSubmitHandler(() => (sender) => afterSubmitFormHandler(sender));
      postSubmitHandlerRegistered.current = true;
    }
  }, [recordId, registerPostSubmitHandler]);

  const uploadAttachments = async () => {
    const recordData = getRecordData?.();
    setAreFilesUploading(true);
    Promise.allSettled(
      attachmentRef.current
        .filter((file) => !uploadedFileNamesRef.current.includes(file.name))
        .map((attachment, index) => {
          return getPresignedUrl(index) //index used to invalidate cache
            .unwrap()
            .then((result: string) => {
              return fetch(result, {
                method: 'PUT',
                body: attachment,
              })
                .then(() => ({ presignedUrl: result, fileName: attachment.name }))
                .catch((error) => {
                  throw { error, fileName: attachment.name };
                });
            });
        })
    ).then(async (settledResults: PromiseSettledResult<PresignedUrlResult>[]) => {
      const fulfilledResults = settledResults
        .filter(
          (res): res is PromiseFulfilledResult<PresignedUrlResult> =>
            res.status === 'fulfilled' && !('error' in res.value)
        )
        .map((res) => res.value);
      uploadedFileNamesRef.current = [
        ...uploadedFileNamesRef.current,
        ...fulfilledResults.map((item) => item.fileName),
      ];
      preUploadedAttachmentsRef.current = [
        ...preUploadedAttachmentsRef.current,
        ...fulfilledResults.map((item) => ({
          ...item,
          originalRecordStatus: recordData.status as string,
          questionId,
        })),
      ];
      updateRecordDataToIncludeAttachments?.(
        questionId
          ? { questionnaireConfirmations: preUploadedAttachmentsRef.current }
          : { confirmations: preUploadedAttachmentsRef.current }
      );
      setAreFilesUploading(false);
    });
  };

  const deleteAttachments = async (recordId: number) => {
    const deletedItems: Array<DeletedAttachmentItem> = [];

    for (let i = 0, length = deletedAttachmentsRef.current?.length; i < length; i++) {
      try {
        await deleteAttachment({
          id: deletedAttachmentsRef.current[i],
          recordId,
          questionId,
        }).unwrap();

        deletedItems.push({ id: deletedAttachmentsRef.current[i] });
      } catch (error) {
        console.error(error);
      }
    }
    deletedAttachmentsRef.current = [];
    return deletedItems;
  };

  const processDeletedAttachments = (deletedItems: Array<DeletedAttachmentItem>) => {
    return new Promise<JsonDataItem | undefined>((resolve) => {
      let result;

      if (deletedItems.length > 0) {
        const deleteItemIds = deletedItems.map((item) => String(item.id));
        // store updates
        result = {
          id, // field id
          questionId,
          deletedAttachmentIds: deleteItemIds, // deleted attachment IDs
        };
      }

      resolve(result);
    });
  };

  const beforeSubmitFormHandler = (recordId: number, sender: OdinDataSender<JsonDataItem>) => {
    deleteAttachments(recordId)
      .then(processDeletedAttachments)
      .then((deletedAttachments) => {
        const attachments = deletedAttachments ? { removed: deletedAttachments } : {};
        sender.sendFinalData(attachments);
      })
      .finally(() => {
        sender.sendFinalData();
      });
  };

  const afterSubmitFormHandler = (sender: OdinDataSender<void>) => {
    attachmentRef.current = [];
    preUploadedAttachmentsRef.current = [];
    uploadedFileNamesRef.current = [];
    storedObjectKeysRef.current = [];
    fileRef.current?.resetFiles();
    sender.sendFinalData();
  };

  const handleAttachmentDelete = (id: string) => {
    deletedAttachmentsRef.current.push(Number(id));
    onChange?.({ id: Number(id) });
  };

  const handleChange = (newValue?: Array<File>) => {
    attachmentRef.current = newValue || [];
    preUploadedAttachmentsRef.current = preUploadedAttachmentsRef.current.filter((file) =>
      newValue?.some((updatedFiles) => updatedFiles.name === file.fileName)
    );
    uploadedFileNamesRef.current = uploadedFileNamesRef.current.filter((fileName) =>
      newValue?.some((file) => file.name === fileName)
    );
    storedObjectKeysRef.current = storedObjectKeysRef.current.filter((storedFile) =>
      newValue?.some((file) => file.name === storedFile.fileName)
    );
    onChange?.(newValue);
    uploadAttachments();
  };

  return (
    <>
      {isUploadingOrLoadingFiles && <OdinLoadingIndicator label={areFilesUploading ? t('uploading') : t('loading')} />}
      <FileUpload
        classNames="mb-2 mt-4"
        maxUploadFiles={20}
        maxUploadFileSize={maxUploadFileSize}
        label={label}
        error={error}
        readOnly={readOnly}
        onChange={handleChange}
        ref={fileRef}
        labelStyle={labelStyle}
        thumbnails={thumbnails}
        required={required}
        containerStyles={questionId ? 'min-h-[100px]' : ''}
        isUploading={areFilesUploading}
      />
      {readOnly && attachments?.length === 0 && <EmptyFieldLabel />}
      {attachments && (
        <AttachmentList
          attachments={forceAssert<Array<AttachmentFileProps>>(attachments)}
          thumbnails={thumbnails}
          readOnly={readOnly as boolean}
          onAttachmentDelete={handleAttachmentDelete}
        />
      )}
    </>
  );
};

export default AttachmentsField;
